import { useId } from "react";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

interface MetricChartProps {
  type?: "area" | "bar" | "line";
  data: Record<string, unknown>[];
  dataKeys: { key: string; label?: string; color?: string }[];
  xKey: string;
  height?: number;
  showGrid?: boolean;
  showLegend?: boolean;
  className?: string;
}

const DEFAULT_COLORS = [
  "#EC5B13",
  "#6366f1",
  "#22c55e",
  "#f59e0b",
  "#06b6d4",
];

/* Custom tooltip */
function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-xl shadow-lg px-3 py-2.5 text-xs">
      <p className="font-bold text-[#0f172a] mb-1.5">{label}</p>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="size-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-[#64748b]">{p.name ?? p.dataKey}:</span>
          <span className="font-bold text-[#0f172a]">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

export function MetricChart({
  type = "area",
  data,
  dataKeys,
  xKey,
  height = 200,
  showGrid = true,
  showLegend = false,
  className = "",
}: MetricChartProps) {
  // Unique prefix per instance so SVG gradient IDs never collide across charts
  const uid = useId().replace(/:/g, "");

  const axisStyle = {
    fill: "#94a3b8",
    fontSize: 11,
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 500,
  };

  const renderChart = () => {
    const commonProps = {
      data,
      margin: { top: 4, right: 8, left: -16, bottom: 0 },
    };

    if (type === "bar") {
      return (
        <BarChart {...commonProps}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />}
          <XAxis dataKey={xKey} tick={axisStyle} axisLine={false} tickLine={false} />
          <YAxis tick={axisStyle} axisLine={false} tickLine={false} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(241,245,249,0.6)" }} />
          {showLegend && (
            <Legend
              wrapperStyle={{ fontSize: 11, fontFamily: "'Montserrat', sans-serif", paddingTop: 8 }}
              iconType="circle"
              iconSize={7}
            />
          )}
          {dataKeys.map((dk, i) => (
            <Bar
              key={dk.key}
              dataKey={dk.key}
              name={dk.label ?? dk.key}
              fill={dk.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length]}
              radius={[4, 4, 0, 0]}
              maxBarSize={24}
            />
          ))}
        </BarChart>
      );
    }

    if (type === "line") {
      return (
        <LineChart {...commonProps}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />}
          <XAxis dataKey={xKey} tick={axisStyle} axisLine={false} tickLine={false} />
          <YAxis tick={axisStyle} axisLine={false} tickLine={false} />
          <Tooltip content={<CustomTooltip />} />
          {showLegend && <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="circle" iconSize={7} />}
          {dataKeys.map((dk, i) => (
            <Line
              key={dk.key}
              type="monotone"
              dataKey={dk.key}
              name={dk.label ?? dk.key}
              stroke={dk.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length]}
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 5, strokeWidth: 0 }}
            />
          ))}
        </LineChart>
      );
    }

    // Area chart - gradient IDs are prefixed with uid to prevent collisions
    return (
      <AreaChart {...commonProps}>
        <defs>
          {dataKeys.map((dk, i) => {
            const color = dk.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length];
            return (
              <linearGradient key={dk.key} id={`${uid}-grad-${dk.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.2} />
                <stop offset="100%" stopColor={color} stopOpacity={0.01} />
              </linearGradient>
            );
          })}
        </defs>
        {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />}
        <XAxis dataKey={xKey} tick={axisStyle} axisLine={false} tickLine={false} />
        <YAxis tick={axisStyle} axisLine={false} tickLine={false} />
        <Tooltip content={<CustomTooltip />} />
        {showLegend && <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="circle" iconSize={7} />}
        {dataKeys.map((dk, i) => {
          const color = dk.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length];
          return (
            <Area
              key={dk.key}
              type="monotone"
              dataKey={dk.key}
              name={dk.label ?? dk.key}
              stroke={color}
              strokeWidth={2.5}
              fill={`url(#${uid}-grad-${dk.key})`}
              activeDot={{ r: 5, strokeWidth: 0, fill: color }}
            />
          );
        })}
      </AreaChart>
    );
  };

  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height={height}>
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
}